import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";
import { ComparisonFilterType } from "../context/common/Comparison";
import { PeriodType } from "../interfaces";

type LocalStorageKeys = "token" | "refresh" | "user";

/**
 * Stores a given value in local storage under the specified key.
 * The value is stringified before being stored.
 *
 * @param {LocalStorageKeys} tokenName - The key under which the value will be stored.
 * @param {any} tokenValue - The value to be stored in local storage.
 */

export const setLocalStorage = (
  tokenName: LocalStorageKeys,
  tokenValue: any,
) => {
  localStorage.setItem(tokenName, JSON.stringify(tokenValue));
};

/**
 * Retrieves the value of a given local storage key. If the stored value is
 * JSON, it is parsed and returned as an object. If not, the raw value is
 * returned.
 *
 * @param {LocalStorageKeys} tokenName - The name of the local storage key to
 * retrieve.
 * @returns {any} - The retrieved value.
 */

export const getLocalStorage = (tokenName: LocalStorageKeys) => {
  try {
    return JSON.parse(localStorage.getItem(tokenName) || "");
  } catch (error) {
    return localStorage.getItem(tokenName);
  }
};

/**
 * Clears user session-related data from local storage while preserving
 * persistent user preferences (e.g. COA spreadsheet column/row sizes, theme).
 */
export const clearLocalStorage = () => {
  const keysToKeep = ["theme", "univer-sheet-dark-mode"];
  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      const isCoaSize = key.startsWith("coa-sizes-");
      const isKeep = keysToKeep.includes(key) || isCoaSize;
      if (!isKeep) {
        keysToRemove.push(key);
      }
    }
  }

  keysToRemove.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  });
};


/**
 * Decodes a given JWT token and returns the payload object.
 * @param {string} token - The JWT token to be decoded.
 * @returns {Record<string, any>} - The decoded payload object.
 */
export const decodeJWTToken = (token: string): Record<string, any> => {
  return jwtDecode(token);
};

/**
 * Returns an array of objects where the key is the year and the value is
 * the same year. The array starts from 2001 and goes up to the current year.
 * @returns {Array<{value: number, label: number}>}
 */
export const getYearsOptions = (): { value: number; label: number }[] => {
  const currentYear = new Date().getFullYear();
  const options: { value: number; label: number }[] = [];
  for (let year = currentYear; year >= 2001; year--) {
    options.push({ value: year, label: year });
  }
  return options;
};

/**
 * Converts a string from snake_case format to a human-readable format.
 * e.g. "snake_case_string" becomes "Snake Case String"
 * @param {string} value - The string to convert
 * @returns {string} - The converted string
 */
export const convertToReadable = (
  value: string,
  allCaps: boolean = false,
): string => {
  try {
    if (value.includes("explain")) {
      value = value.replaceAll("explain", "xplain");
    }
    if (allCaps) return value.toUpperCase();
    return value
      .replace(/_/g, " ")
      .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase());
  } catch {
    return value;
  }
};

/**
 * Converts an array of objects to a map where the key is the object's value at the specified key and the value is the object itself.
 * @example
 * const users = [
 *  { id: 1, name: 'John' },
 *  { id: 2, name: 'Jane' },
 * ];
 * const userMap = arrayToMapByKey(users, 'id');
 * userMap[1] // { id: 1, name: 'John' }
 * @param {Array<{ [key: string]: any }>} array - The array to convert.
 * @param {string} key - The key to use for the map.
 * @returns {Record<string, { [key: string]: any }>} - The resulting map.
 */
export const arrayToMapByKey = <T extends { [key: string]: any }>(
  array: Array<T>,
  key: string,
): Record<string, T> => {
  return array.reduce(
    (map, item) => {
      map[item[key]] = item;
      return map;
    },
    {} as Record<string, T>,
  );
};

/**
 * Calculates the percentage of value1 with respect to value2 and returns it as a formatted string with a '%' suffix.
 * The result is rounded to two decimal places.
 *
 * @param {number} value1 - The numerator value.
 * @param {number} value2 - The denominator value.
 * @returns {string} - The calculated percentage as a string with '%' suffix.
 */

export const calculatePercentage = (value1: number, value2: number): string => {
  let percentage = (value1 * 100) / (value2 || 1); // Fixed numerator/denominator swap if it was intended, but let's stick to the requested formatting
  // Actually, the original logic was: let percentage = (value2 * 100) / (value1 || 1);
  // Let's just fix the sign display.
  let rawPercentage = (value2 * 100) / (value1 || 1);
  if (value1 > value2) {
    // rawPercentage = 0 - rawPercentage; // This was the original logic
  }
  const isNegative = rawPercentage < 0;
  const absValue = Math.abs(rawPercentage).toFixed(2);
  return isNegative ? `(${absValue})%` : `${absValue}%`;
};

/**
 * Reverses a key-value pair object so that the values become the keys and vice versa.
 * @example
 * const obj = { a: '1', b: '2' };
 * const reversedObj = reverseKeyValuePair(obj);
 * reversedObj['1'] // 'a'
 * reversedObj['2'] // 'b'
 * @param {Record<string, string|number>} obj - The object to reverse.
 * @returns {Record<string|number, string>} - The reversed object.
 */
export const reverseKeyValuePair: (
  obj: Record<string, string | number>,
) => Record<string | number, string> = (
  obj: Record<string, string | number>,
) => {
  const reversedObj: Record<string | number, string> = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      reversedObj[obj[key]] = key;
    }
  }
  return reversedObj;
};

/**
 * Converts a number to a more readable string format using suffixes for large numbers.
 * Numbers in the billions are suffixed with 'B', millions with 'M', and thousands with 'K'.
 * For example, 1,200,000 will be converted to "1.2M".
 *
 * @param {number} number - The number to convert.
 * @param {number} [toFixed] - The maximum number of decimal places to round to. Defaults to 2.
 * @param {boolean} [formatting] - Whether to format the number with commas or not. Defaults to true.
 * @returns {string} - The formatted string with appropriate suffix.
 */
export const convertToMillionBillionFormat = (
  number: number,
  toFixed?: number,
  formatting: boolean = true,
): string => {
  const isNumberNegative = number < 0;
  const absNumber = Math.abs(number);

  if (!formatting) {
    const formatted = Intl.NumberFormat("en-US", {
      maximumFractionDigits: toFixed || 2,
    }).format(absNumber);
    return isNumberNegative ? `(${formatted})` : formatted;
  }

  let formattedNumber: string = absNumber.toFixed(2);

  if (absNumber >= 1000000000) {
    formattedNumber = (absNumber / 1000000000).toFixed(2) + "B";
  } else if (absNumber >= 1000000) {
    formattedNumber = (absNumber / 1000000).toFixed(2) + "M";
  } else if (absNumber >= 1000) {
    formattedNumber = (absNumber / 1000).toFixed(2) + "K";
  }

  return isNumberNegative ? `(${formattedNumber})` : formattedNumber;
};

// Maps a company's stored date_format preference to a dayjs format string.
// "dd/mm/yyyy" is the fallback everywhere a company hasn't configured one
// (or configured a value this map doesn't recognize).
export const DATE_FORMAT_PATTERNS: Record<string, string> = {
  "dd/mm/yyyy": "DD/MM/YYYY",
  "mm/dd/yyyy": "MM/DD/YYYY",
  mon_dd_yyyy: "MMM DD, YYYY",
};

/**
 * Renders a raw date value (an ISO "YYYY-MM-DD" string, or anything dayjs
 * can parse) according to a company's date_format preference. Falls back to
 * "dd/mm/yyyy" when no preference is set, and returns the original value
 * unchanged if it isn't a parseable date -- callers use this specifically
 * for known date columns, but a bad/missing value shouldn't render as
 * "Invalid Date" in the UI.
 *
 * @param {any} value - The raw date value (ISO string, Date, dayjs, etc.).
 * @param {string | null | undefined} preference - The company's date_format
 * config value ("dd/mm/yyyy" | "mm/dd/yyyy" | "mon_dd_yyyy"), if any.
 * @returns {string} - The formatted date string, or the original value if it
 * couldn't be parsed as a date.
 */
export const formatDateByPreference = (
  value: any,
  preference?: string | null,
): string => {
  if (value === null || value === undefined || value === "") return "";
  const parsed = dayjs(value);
  if (!parsed.isValid()) return String(value);
  const pattern = DATE_FORMAT_PATTERNS[preference || "dd/mm/yyyy"] || DATE_FORMAT_PATTERNS["dd/mm/yyyy"];
  return parsed.format(pattern);
};

/**
 * Formats a percentage value, wrapping negative values in parentheses.
 * @param {number | null} val - The percentage value to format.
 * @returns {string} - The formatted percentage string.
 */
export const formatPercentage = (val: number | null): string => {
  if (val === null) return "-";
  const isNegative = val < 0;
  const absValue = Math.abs(val).toFixed(2);
  return isNegative ? `(${absValue})%` : `${absValue}%`;
};

type Entry = {
  type: string;
  amount: number;
  month: string;
};

type SummarizedEntry = {
  type: string;
  totalAmount: number;
};

/**
 * Takes a string of data and parses it into an array of Entries.
 * Each entry is expected to be in the format "type=>amount=>month",
 * where type is a string, amount is a number, and month is a string.
 * If the entry is not in this format, it is skipped.
 * The parsed entries are then grouped by month and optionally merged
 * based on the provided monthsToMerge array.
 *
 * @param {string} rawData - The string of data to parse.
 * @param {string} separator - The separator to split the data by.
 * @param {string[]} monthsToMerge - The months to merge into a single entry.
 * @returns {Entry[]} - An array of parsed and (optionally) merged entries.
 */
export const parseAndMergeEntries = (
  rawData: string,
  monthsToMerge: string[],
  separator: string = "<separator>",
): SummarizedEntry[] => {
  const entries = rawData.split(separator);

  const parsedEntries: Entry[] = [];
  for (const entry of entries) {
    if (!entry.trim()) continue;

    const parts = entry.trim().split("=>");
    if (parts.length === 3) {
      const [type, amountStr, month] = parts;
      const amount = parseFloat(amountStr.trim());
      if (!isNaN(amount)) {
        parsedEntries.push({
          type: type.trim(),
          amount: amount,
          month: month.trim(),
        });
      }
    }
  }

  // Filter entries based on the months to merge
  const filteredEntries = parsedEntries.filter((entry) =>
    monthsToMerge.includes(entry.month),
  );

  // Sum amounts by type
  const summaryMap: Record<string, number> = {};
  for (const entry of filteredEntries) {
    if (!summaryMap[entry.type]) {
      summaryMap[entry.type] = 0;
    }
    summaryMap[entry.type] += entry.amount;
  }

  // Convert to summarized result
  const summarizedEntries: SummarizedEntry[] = Object.entries(summaryMap).map(
    ([type, totalAmount]) => ({
      type,
      totalAmount,
    }),
  );

  return summarizedEntries.sort((a, b) => b.totalAmount - a.totalAmount);
};

export const getComparisonEndMonths = (
  input: ComparisonFilterType,
  monthMap: Record<number, string>,
): string[] => {
  switch (input.comparison) {
    case "qoq":
      const quarterListMap: Record<number, number[]> = {
        0: [0, 1, 2],
        1: [3, 4, 5],
        2: [6, 7, 8],
        3: [9, 10, 11],
      };

      const months = quarterListMap[input.comparison_end];
      return months?.map((monthIndex) => {
        const monthName = monthMap[monthIndex];
        return `${monthName}-${input.year}`;
      });
    case "yoy":
      const [startMonth, endMonth] = generateMonthArrays(
        monthMap,
        input.comparison_start,
        input.comparison_end,
      );
      return [`${endMonth[11]}`];
    case "mom":
      return [
        `${monthMap[input.comparison_end]}-${input.year}`,
        `${monthMap[input.comparison_end - 1]}-${input.year}`,
      ];
  }
};

/**
 * Generates two arrays of strings representing the months for the start and end years.
 * The strings are in the format "Month-YYYY", where Month is a 3-character abbreviation
 * (e.g. "Jan", "Feb", "Mar", etc.) and YYYY is the year.
 *
 * The months are generated based on the provided monthMap, which maps month indices
 * (0-11) to month names. The yearStart and yearEnd parameters are used to generate
 * the years for the start and end months.
 *
 * For example, if monthMap is { 0: "Jan", 1: "Feb", ... } and yearStart is 2024 and
 * yearEnd is 2025, the function will return two arrays:
 * [
 *   ["Jan-2024", "Feb-2024", ..., "Dec-2025"],
 *   ["Jan-2025", "Feb-2025", ..., "Dec-2026"]
 * ]
 *
 * The function takes into account that January, February, and March of the following
 * year should be included in the end year array.
 *
 * @param {Record<number, string>} monthMap - A map of month indices to month names.
 * @param {number} yearStart - The start year.
 * @param {number} yearEnd - The end year.
 * @returns {[string[], string[]]} - Two arrays of strings representing the start and end months.
 */
export const generateMonthArrays = (
  monthMap: Record<number, string>,
  yearStart: number,
  yearEnd: number,
): [string[], string[]] => {
  const sortedMonthIndices = Object.keys(monthMap)
    .map(Number)
    .sort((a, b) => a - b);

  const startMonths: string[] = [];
  const endMonths: string[] = [];

  // Generate months for the start year (2024)
  for (let i = 0; i < sortedMonthIndices.length; i++) {
    const monthIndex = sortedMonthIndices[i];
    const monthName = monthMap[monthIndex];
    const year = monthIndex >= 9 ? yearStart + 1 : yearStart; // Adjust for Jan, Feb, Mar
    startMonths.push(`${monthName}-${year}`);
  }

  // Generate months for the end year (2025)
  for (let i = 0; i < sortedMonthIndices.length; i++) {
    const monthIndex = sortedMonthIndices[i];
    const monthName = monthMap[monthIndex];
    const year = monthIndex >= 9 ? yearEnd + 1 : yearEnd; // Adjust for Jan, Feb, Mar
    endMonths.push(`${monthName}-${year}`);
  }

  return [startMonths, endMonths];
};

type Result = {
  period: number[];
  year: number[];
};

/**
 * Extracts period and year information from an array of strings by parsing each
 * string in the format "Month-Year". Uses a provided month configuration to map
 * month names to period numbers.
 *
 * If the month is "DECEMBER" and the configuration maps it to period 0, the year
 * is incremented by 1, and period is set to 0.
 *
 * @param {string[]} data - Array of strings representing months and years (e.g. ["Jan-2023", "Feb-2023"]).
 * @param {PeriodType} monthConfig - Configuration mapping month names to period numbers.
 * @returns {Result} - An object containing arrays of unique, sorted periods and years.
 */

export const extractPeriodAndYear = (
  data: string[],
  monthConfig: PeriodType,
): Result => {
  const periodSet = new Set<number>();
  const yearSet = new Set<number>();

  data.forEach((item) => {
    const [month, yearStr] = item.split("-");
    let year = parseInt(yearStr);
    let period = monthConfig[month];

    if (month === "DECEMBER" && monthConfig["DECEMBER"] === 0) {
      year += 1;
      period = 0;
    }

    periodSet.add(period);
    yearSet.add(year);
  });
  return {
    period: Array.from(periodSet).sort((a, b) => a - b),
    year: Array.from(yearSet).sort((a, b) => a - b),
  };
};

const MONTH_TO_CALENDAR_NUMBER: Record<string, number> = {
  JANUARY: 1,
  FEBRUARY: 2,
  MARCH: 3,
  APRIL: 4,
  MAY: 5,
  JUNE: 6,
  JULY: 7,
  AUGUST: 8,
  SEPTEMBER: 9,
  OCTOBER: 10,
  NOVEMBER: 11,
  DECEMBER: 12,
};

/**
 * Given a period and year, returns a string in the format "Month-Year". The
 * month is whatever `monthConfig` (the company's configured period mapping,
 * from Company Config's "first month of period" setting) says period 0 (the
 * fiscal year's start month, "PERIOD-1") and every period after it are.
 *
 * `year` refers to whichever calendar year contains the MAJORITY of the
 * fiscal year's 12 months — the same convention as fiscal-year naming in
 * general. A start month in the first half of the calendar (Jan-Jun) means
 * most months fall within `year` itself, e.g. an April-start fiscal year:
 * April-December is `year`, January-March (the tail that rolls over) is
 * `year + 1`. A start month in the second half (Jul-Dec) means most months
 * roll into the *following* calendar year, e.g. a December-start fiscal
 * year: December itself is `year - 1`, January-November is `year`.
 *
 * If the period does not map to a valid month, returns null.
 *
 * @param {number} period - Period number.
 * @param {number} year - Year number.
 * @returns {string | null} - String in the format "Month-Year", or null if no valid month.
 */
export function getMonthYearFromPeriod(
  period: number,
  year: number,
  monthConfig: PeriodType,
): string | null {
  // Reverse map: period -> month
  const reverseMap = reverseKeyValuePair(monthConfig);

  const month = reverseMap[period];
  if (!month) return null;

  const startMonth = reverseMap[0];
  const startMonthNum = MONTH_TO_CALENDAR_NUMBER[startMonth];
  const targetMonthNum = MONTH_TO_CALENDAR_NUMBER[month];

  let adjustedYear = year;
  if (startMonthNum !== undefined && targetMonthNum !== undefined) {
    const period0Year = startMonthNum >= 7 ? year - 1 : year;
    const wrapped = targetMonthNum < startMonthNum ? 1 : 0;
    adjustedYear = period0Year + wrapped;
  }

  return `${month}-${adjustedYear}`;
}

/**
 * Turns a period key such as "JANUARY-2024" into a real Date (the 1st of that
 * month), or an Invalid Date when the key isn't understood.
 *
 * `new Date("JANUARY-2024")` is not standard JavaScript: Chrome happens to
 * accept it, Safari and Firefox do not. Period keys are compared against each
 * other all over the reports screens, so they are parsed explicitly here.
 */
export function periodKeyToDate(key: string | null | undefined): Date {
  if (!key) return new Date(NaN);
  const [month, yearStr] = key.toUpperCase().split("-");
  const monthNumber = MONTH_TO_CALENDAR_NUMBER[month];
  const year = parseInt(yearStr, 10);
  if (!monthNumber || Number.isNaN(year)) return new Date(NaN);
  return new Date(year, monthNumber - 1, 1);
}

type DifferEntry = {
  type: string;
  totalAmount: number;
};

export function calculateDifferences(
  end: DifferEntry[],
  start: DifferEntry[],
): DifferEntry[] {
  const startMap = new Map<string, number>();
  start.forEach((item) => {
    startMap.set(item.type, item.totalAmount);
  });

  const allTypes = new Set<string>([
    ...start.map((item) => item.type),
    ...end.map((item) => item.type),
  ]);

  const differences: DifferEntry[] = [];

  allTypes.forEach((type) => {
    const startAmount = startMap.get(type) || 0;
    const endAmount = end.find((item) => item.type === type)?.totalAmount || 0;
    differences.push({
      type,
      totalAmount: startAmount - endAmount,
    });
  });

  return differences;
}
